// Dummy surveys store, shared between AdminSurveyList and
// AdminSurveyDetail. Replace with backend-backed data in a later
// iteration.
//
// Future endpoint shapes (following the existing apiService pattern):
//
//   apiService.fetchSurveys()                  → GET    /api/seq/surveys
//   apiService.fetchSurveyBySlug(slug)         → GET    /api/seq/surveys/:slug
//   apiService.createSurvey(payload)           → POST   /api/seq/surveys
//   apiService.updateSurvey(id, patch)         → PATCH  /api/seq/surveys/:id
//   apiService.deleteSurvey(id)                → DELETE /api/seq/surveys/:id
//
// Org-scoping note for the eventual backend: seq_surveys has NO
// organization_id column. Ownership reaches the org indirectly via
//   questionnaire_id → seq_questionnaires.organization_id
// so the LIST endpoint will need a JOIN to seq_questionnaires and
// filter by current_user["org_id"]. Same JOIN for slug-uniqueness
// (although slugs are globally unique on this table, so a simple
// SELECT ... WHERE slug = $1 will surface conflicts directly).

import { reactive } from 'vue';

const initialItems = [
  {
    id: 1,
    questionnaire_id: 1,
    name: 'SEQ Main 2026 Launch',
    slug: 'seq-main-2026',
    description: 'Public launch of the SEQ following pilot validation. Promoted via the BFH website and partner channels.',
    starts_at: '2026-04-01T00:00:00.000Z',
    ends_at: '2026-12-31T00:00:00.000Z',
    status: 'active',
    created_at: '2026-03-20T10:00:00Z',
    updated_at: '2026-04-22T14:30:00Z',
    created_by: 86,
  },
  {
    id: 2,
    questionnaire_id: 2,
    name: 'Pilot Round 2 — BE Schools',
    slug: 'pilot-r2-be-schools',
    description: 'Second round of school-based pilot testing in the Canton of Bern. Recruiting via teacher contacts.',
    starts_at: null,
    ends_at: null,
    status: 'draft',
    created_at: '2026-04-28T09:15:00Z',
    updated_at: '2026-05-09T11:42:00Z',
    created_by: 86,
  },
  {
    id: 3,
    questionnaire_id: 3,
    name: 'SEQ 2025 Pilot — Autumn',
    slug: 'seq-2025-pilot-run',
    description: 'Autumn 2025 pilot run, kept for historical comparison.',
    starts_at: '2025-09-15T00:00:00.000Z',
    ends_at: '2025-12-15T00:00:00.000Z',
    status: 'closed',
    created_at: '2025-09-12T08:00:00Z',
    updated_at: '2025-12-31T17:00:00Z',
    created_by: 86,
  },
  {
    id: 4,
    questionnaire_id: 4,
    name: 'Workshop — Spring 2026',
    slug: 'workshop-spring-2026-run',
    description: '',
    starts_at: '2026-03-01T00:00:00.000Z',
    ends_at: '2026-06-30T00:00:00.000Z',
    status: 'active',
    created_at: '2026-02-15T13:20:00Z',
    updated_at: '2026-05-08T16:55:00Z',
    created_by: 86,
  },
];

export const surveysState = reactive({
  items: initialItems.slice(),
});

export function findSurveyBySlug(slug) {
  return surveysState.items.find(s => s.slug === slug);
}

export function findSurveyById(id) {
  return surveysState.items.find(s => s.id === id);
}

// Survey slugs are GLOBALLY unique (not per-org like questionnaires) —
// the public URL /s/<slug> has no other context. excludingId lets edit
// flows allow the row's own slug.
export function isSurveySlugTaken(slug, excludingId = null) {
  return surveysState.items.some(
    s => s.slug === slug && s.id !== excludingId,
  );
}

export function addSurvey(payload) {
  const nextId = surveysState.items.reduce((max, s) => Math.max(max, s.id), 0) + 1;
  const now = new Date().toISOString();
  const item = {
    id: nextId,
    questionnaire_id: payload.questionnaire_id,
    name: payload.name,
    slug: payload.slug,
    description: payload.description || '',
    starts_at: payload.starts_at || null,
    ends_at: payload.ends_at || null,
    status: payload.status || 'draft',
    created_at: now,
    updated_at: now,
    created_by: 0,
  };
  surveysState.items.unshift(item);
  return item;
}

export function replaceSurvey(updated) {
  if (!updated || updated.id == null) return;
  const idx = surveysState.items.findIndex(s => s.id === updated.id);
  if (idx !== -1) {
    surveysState.items.splice(idx, 1, updated);
  }
}

export function upsertSurvey(item) {
  if (!item || item.id == null) return;
  const idx = surveysState.items.findIndex(s => s.id === item.id);
  if (idx === -1) {
    surveysState.items.push(item);
  } else {
    surveysState.items.splice(idx, 1, item);
  }
}

export function removeSurvey(id) {
  const idx = surveysState.items.findIndex(s => s.id === id);
  if (idx !== -1) {
    surveysState.items.splice(idx, 1);
    return true;
  }
  return false;
}

// Slug constraint from the schema (seq_surveys_slug_format CHECK):
//   ^[a-z][a-z0-9_-]{1,63}$
// Same rule as questionnaires.
export const SLUG_REGEX = /^[a-z][a-z0-9_-]{1,63}$/;

// ---------- date helpers ----------
// HTML5 <input type="date"> uses YYYY-MM-DD strings; the database stores
// TIMESTAMPTZ. The convention in this codebase is to interpret a chosen
// day as midnight UTC. Both conversions live here so the modal and the
// list/detail pages stay in sync.

export function dateInputToIso(dateStr) {
  if (!dateStr) return null;
  return new Date(`${dateStr}T00:00:00.000Z`).toISOString();
}

export function isoToDateInput(iso) {
  if (!iso) return '';
  return iso.slice(0, 10);
}

const dateLongFmt = new Intl.DateTimeFormat('en-GB', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
});

const dateShortFmt = new Intl.DateTimeFormat('en-GB', {
  day: 'numeric',
  month: 'short',
});

export function formatDateLong(iso) {
  if (!iso) return '';
  return dateLongFmt.format(new Date(iso));
}

// "1 Apr – 31 Dec 2026"  | "Open from 1 Apr 2026"  | "Open until 31 Dec 2026"
// "No time window"       | "1 Apr 2025 – 31 Dec 2026" (different years)
export function formatTimeWindow(startsAt, endsAt) {
  if (!startsAt && !endsAt) return 'No time window';
  if (startsAt && !endsAt) return `Open from ${formatDateLong(startsAt)}`;
  if (!startsAt && endsAt) return `Open until ${formatDateLong(endsAt)}`;

  const startYear = new Date(startsAt).getUTCFullYear();
  const endYear = new Date(endsAt).getUTCFullYear();

  if (startYear === endYear) {
    return `${dateShortFmt.format(new Date(startsAt))} – ${formatDateLong(endsAt)}`;
  }
  return `${formatDateLong(startsAt)} – ${formatDateLong(endsAt)}`;
}
